import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { LayerApi } from '../../../api/layer-api';
import { util } from '../../../classes/util';
import { BooleanConverter, InputConverter, NumberConverter } from '../../../inputConverter';
import { keys } from '../../../keys';
import { ExportService } from '../../../services/export.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../services/layer.service';
import { StorageService } from '../../../services/storage.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { ColumnSortOrder, IColumnSetting, IColumnSettingsStorage, IExportParameters, IFilterSetting, ILayer, ILayerColumn, ILayerSettings, IMapcoreProps, IOperatorOption, IQuery, ISmallColumnSetting, ISortSetting, LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';
import { ITableColumnSettings, RowClass, TableCellDataType } from '../../utilityComponents/table/table-types';
/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-data-table-ng',
    styleUrls: [
        '../../sharedStyles/base.scss',
        './data-table.component.scss'
    ],
    templateUrl: './data-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent extends BaseComponent implements AfterViewInit {
    _columnSettings: IColumnSetting[] = [];
    _columnSortOrder: ColumnSortOrder = 'database';
    _opened: boolean = false;
    _mapcoreProps: IMapcoreProps[] = [];

    get visibleGridColumns(): ITableColumnSettings[] {
        return this.tableColumnSettings ? this.tableColumnSettings.filter(c => c.isVisible) : [];
    }

    get rowCountNumber() {
        return !!this.rowCount ? parseInt(this.rowCount.replace(/,/g, ''), 10) : 0;
    }

    get canDelete() { return this.availableCababilities.includes('DR'); }

    get canInsert() { return this.availableCababilities.includes('IR'); }

    get canUpdate() { return this.availableCababilities.includes('UR'); }

    @Input()
    get columnSortOrder() { return this._columnSortOrder; }
    set columnSortOrder(val: ColumnSortOrder) {
        if (val !== this._columnSortOrder) {
            this._columnSortOrder = val;
            (async () => {
                await this.accessingLocalStorage;
                if (this.updateStorage) {
                    const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);
                    if (hasKey) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'sortBy', this._columnSortOrder, 'data-table');
                    } else {
                        await this.createOrUpdateFromLocalStorage('order');
                    }
                }
                this.setColumnSettingsFilteredForDialog();
            })();
        }
    }

    @Input()
    get columnSettings() { return this._columnSettings; }
    set columnSettings(val: IColumnSetting[]) {
        if (val !== this._columnSettings) {
            this._columnSettings = val;
            (async () => {
                await this.accessingLocalStorage;
                if (this.updateStorage) {
                    const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);
                    if (hasKey) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'columnSettings', this.mapColumnsForSave(this._columnSettings), 'data-table');
                    } else {
                        await this.createOrUpdateFromLocalStorage('columnSettings');
                    }
                }
                this.setColumnCheckboxes();
                this.setColumnSettingsFilteredForDialog();
            })();
        }
    }

    @Input()
    @InputConverter(BooleanConverter)
    get opened() { return this._opened; }
    set opened(val: boolean) {
        this._opened = val;
        if (this._opened) {
            if (this.mapcoreProps && this.mapcoreProps.dataTableArr && this.mapcoreProps.dataTableArr[this.mapcorePropsIndex] && !this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord) {
                this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord = this.defaultRecordsFetched;
            }

            window.addEventListener('itemInserted', this.storageUpdated.bind(this));
        } else {
            window.removeEventListener('itemInserted', this.storageUpdated.bind(this));
        }
    }

    @Input()
    get mapcoreProps() {
        return this._mapcoreProps[this.mapcorePropsIndex];
    }
    set mapcoreProps(mapcoreProps: IMapcoreProps) {
        const newEsriLayerId: string = mapcoreProps.esriLayerId;

        this.gridDataResult = null;
        this.tableColumnSettings = [];
        this.showSelected = false;
        this._columnSortOrder = 'database';
        this.changeDetectorRef.detectChanges();

        //find if the layer has already been added
        const i = this._mapcoreProps.findIndex(x => newEsriLayerId === x.esriLayerId);
        if (i > -1) {
            //already exist
            this.mapcorePropsIndex = i;
        } else {
            //create new entry
            this._mapcoreProps.push(mapcoreProps);
            this.mapcorePropsIndex = this._mapcoreProps.length - 1;
        }

        if (this._mapcoreProps[this.mapcorePropsIndex].dataTableArr && this._mapcoreProps.length > 1) {
            this.enableMultiTabs = true;
            this.multiTabsArr = this._mapcoreProps[this.mapcorePropsIndex].dataTableArr;
            this.curMultiTab = newEsriLayerId;
        } else {
            this.enableMultiTabs = false;
            this.multiTabsArr = [];
            this.curMultiTab = '';
        }

        if (this.mapcoreProps.state === undefined) {
            this.mapcoreProps.state = {};
        }

        if (!mapcoreProps.isExternal && mapcoreProps.permissions && this.userPermissions && this.userPermissions.length) {
            this.availableCababilities = mapcoreProps.permissions.filter(per => this.userPermissions.includes(per.name)).map(per => per.action);
        }

        this.refreshGridData().then(async () => {
            await this.createOrUpdateFromLocalStorage();
            this.changeDetectorRef.detectChanges();
        });
    }

    @Input() @InputConverter(BooleanConverter) columnSettingsDialogOpened = null;
    @Input() dataFetched: { [key: string]: DataItem }[] = [];
    @Input() @InputConverter(NumberConverter) delayForFetchingNewColumnData: number = 800;
    @Input() layerColumns: ILayerColumn[] = [];
    @Input() @InputConverter(NumberConverter) pageSize: number = 100;
    @Input() @InputConverter(BooleanConverter) recordEditorEnabled = false;
    @Input() @InputConverter(NumberConverter) maxDefaultColumns: number = 10; // When insufficient columnSettings are supplied. We fill up the tableColumnSettings to this number.
    @Input() @InputConverter(NumberConverter) maxRecordsFetched: number = 10000000;
    @Input() @InputConverter(NumberConverter) defaultRecordsFetched: number = 5000;
    @Input() @InputConverter(BooleanConverter) enableAdditionalInformation: boolean = false;

    @Output() public readonly closeTab = new EventEmitter<string>();
    @Output() public readonly ondrawcomplete = new EventEmitter<void>();
    @Output() public readonly ondraw = new EventEmitter<void>();
    @Output() public readonly openEditor = new EventEmitter<{ layerId: string; objectId?: number }>();

    @ViewChild('lassoTool') lassoTool: any;

    geoSnapTableId: number = 161;
    aggregates: IAggregateField[] = [];
    childTableData: any[] = [];
    tableColumnSettings: ITableColumnSettings[] = [];
    columnSettingsFilterText: string = '';
    columnSettingsSortField: SettingSortType = 'order';
    columnSettingsSortDescending: boolean = false;
    columnSettingsTimer?: number;
    columnSettingBeingDragged: IColumnSetting;
    columnSettingElementBeingDragged: any;
    gridDataResult: any[] = null;
    filteredData: any[] = [];
    filterSettings: IFilterSetting[] = [];
    fullData: any[] = [];
    idPropertyName = 'objectid';
    layerRef: ILayer;
    loading: boolean;
    mouseDownStartX: number;
    mouseDownStartY: number;
    objectIdColumnName: string;
    operatorOptions: IOperatorOption[];
    showFooter: boolean = false;
    rowCount: string = '';
    selectedObjectIds: number[] = [];
    showSelected: boolean = false;
    sortSettings: ISortSetting[] = [];
    parentTabs; //: TabsComponent; // todo TFS#13854
    parentTabName: string;
    localStorageService: StorageService;
    updateStorage: boolean = true;
    layerChildTables: any[] = null;
    childTableChildParentKeyColumnNames: string[] = null;
    featureChildTables: any[] = null;
    tableIdToTableDictionary: { [tableId: number]: any[] } = {};
    showCloseOptions = false;
    hideCustomSort: boolean = true;
    enableMultiTabs = false;
    multiTabsArr: { name: string; layerId: string; maxFetchRecord?: number }[] = [];
    curMultiTab: string;
    maxRecordFetchList = [
        { name: '100', value: 100 },
        { name: '500', value: 500 },
        { name: '1,000', value: 1000 },
        { name: '5,000', value: 5000 },
        { name: '10,000', value: 10000 },
        { name: '50,000', value: 50000 },
        { name: '100,000', value: 100000 }];
    mapcorePropsIndex = 0;
    availableCababilities: string[] = [];
    userPermissions: string[] = [];
    titleProperty: string = 'name';
    mapView: NonNullable<__esri.MapView>;
    columnSettingsFilteredForDialog: IColumnSetting[] = [];
    allVisible = false;
    allSum = false;
    allAverage = false;

    private accessingLocalStorage: Promise<void> = Promise.resolve();

    /* tslint:disable:max-file-line-count */
    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly exportService: ExportService,
        private readonly layerService: LayerService,
        private readonly dataDictionaryApi: DataDictionaryApi,
        public toSqlStringService: ToSqlStringService,
        private readonly layerApi: LayerApi,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public userSettingsService: IUserSettingsService,
        @Inject('authenticationService') public authService: IAuthenticationService
    ) {
        super(_vcr, true);
        this.registerEvent('closeTab', this.closeTab);
        this.localStorageService = new StorageService({ userSettingsService, authService });
        this.authService.permissions.subscribe(permissions => this.userPermissions = permissions || []);
    }

    async ngAfterViewInit() {
        this.mapView = await this.setupMapViewRef(this.mapView);
    }

    private async storageUpdated(e: CustomEvent<{ key: string; caller?: string }>) {
        if ((!e.detail.caller || e.detail.caller !== 'data-table') && e.detail.key.startsWith(keys.storage.columnSettingsStorage.slice(0, keys.storage.columnSettingsStorage.indexOf('{') - 1))) {
            if (!this.updateStorage) {
                await util.retryUntilValue(() => this.updateStorage ? true : undefined, 2000, 100);
            }
            if (this.updateStorage) {
                this.updateStorage = false;
                await this.createOrUpdateFromLocalStorage();
                this.refreshColumnSettings();
                await this.refreshGridData();
                this.columnSettingChange();
                this.updateStorage = true;
            }
        }
    }

    private async setHashedUrl() {
        if (this.mapcoreProps && this.mapcoreProps.url) {
            const hashBuffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(this.mapcoreProps.url));
            this.mapcoreProps.hashedUrl = Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
        }
    }

    @Input()
    public refreshGrid = () => {
        this.refreshGridData();
    }

    async refreshGridData() {
        this.toggleLoading(true);
        this.rowCount = '';
        if (this.mapcoreProps !== undefined) {
            await this.updateLayerInformationIfNeeded();
            await this.fetchDataForAllVisibleColumns();
            this.loadDataIntoGrid();
            this.populateAggregates();
        }
        this.toggleLoading(false);
    }

    selectForCheckBox(checkBoxIdentifier, event) {
        switch (checkBoxIdentifier) {
            case 'visibility':
                this.columnSettings.forEach(col => col.visible = event.target.checked);
                break;
            case 'sum':
                this.columnSettings.filter(col => col.isNumeric && col.calculateSum !== null).forEach(col => col.calculateSum = event.target.checked);
                break;
            case 'average':
                this.columnSettings.filter(col => col.isNumeric && col.calculateAverage !== null).forEach(col => col.calculateAverage = event.target.checked);
                break;
            default:
                break;
        }
        this.columnSettingChange();
    }

    setColumnSettingsFilteredForDialog() {
        if (this.columnSettingsFilterText.length) {
            const searchText = this.columnSettingsFilterText.toLowerCase();
            this.columnSettingsFilteredForDialog = this.columnSettings.filter(x => x.name.toLowerCase().indexOf(searchText) >= 0 || x.displayName.toLowerCase().indexOf(searchText) >= 0);
        } else {
            this.columnSettingsFilteredForDialog = [...this.columnSettings];
        }
        this.sortColumns(this.columnSettingsFilteredForDialog);
        this.changeDetectorRef.detectChanges();
    }

    get hasChildTables() {
        return this.layerChildTables && this.layerChildTables.length > 0; // TODO: We need to know if this individual feature actually has data in any childTables
    }

    get systemLayerId() {
        let systemLayerId = 0;

        if (this.mapcoreProps) {
            systemLayerId = this.mapcoreProps.id || this.mapcoreProps.idFromSavedLayer || this.mapcoreProps.parentId;
        }

        return systemLayerId;
    }

    get tableId() {
        if (this.mapcoreProps && this.mapcoreProps.tables && this.mapcoreProps.tables[0].id) {
            return this.mapcoreProps.tables[0].id;
        }
        return 0;
    }

    get columnSettingsStorageKey() {
        let storageKey: string;
        if (this.mapcoreProps) {
            const projectIdClause = this.toSqlStringService.getPetlProjectIdClauseIfPresent(this.mapcoreProps.parentId, this.mapcoreProps.query, this.mapcoreProps.isPETL);
            if (projectIdClause) {
                // use a different storage key for PETL layers
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'petl-' + projectIdClause.value);
            } else if (!this.mapcoreProps.isExternal) {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'table-' + this.tableId.toString());
            } else {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'ex-' + this.mapcoreProps.hashedUrl);
            }
        }
        return storageKey;
    } //**TODO Handle non-mapcore layers**

    get width() {
        return window.innerWidth;
    }

    addCommas(value: number | string) {
        return (typeof value === 'string' ? parseFloat(value) : value).toLocaleString();
    }

    calculateWidth(columnSetting: IColumnSetting): number {
        const displayName = columnSetting.displayName;
        let wideCount = 0;
        let normalCount = 0;
        for (let i = 0; i < displayName.length; i++) {
            const ch = displayName.charAt(i);
            if (
                (ch >= 'A' && ch <= 'Z' && ch !== 'I') ||
                ch === 'm' ||
                ch === 'w'
            ) {
                wideCount++;
            } else {
                normalCount++;
            }
        }
        const startingWidth = 50;
        const pixelsPerWideChar = 12;
        const pixelsPerNormalChar = 7;
        return (
            startingWidth +
            wideCount * pixelsPerWideChar +
            normalCount * pixelsPerNormalChar
        );
    }

    @Input()
    public close = (esriLayerId?: string) => { // This method needs refactored
        if (esriLayerId) {
            const closeIndex = this._mapcoreProps.findIndex(p => p.esriLayerId === esriLayerId);
            if (closeIndex > -1) {
                this.closeTab.emit(esriLayerId);
                this._mapcoreProps.splice(closeIndex, 1);
                if (closeIndex === this.mapcorePropsIndex && this.mapcorePropsIndex >= this._mapcoreProps.length) {
                    this.mapcorePropsIndex = this._mapcoreProps.length - 1;
                }
                if (this._mapcoreProps.length < 2) {
                    this.enableMultiTabs = false;
                    this.multiTabsArr = [];
                    this.curMultiTab = '';
                }
                if (!this._mapcoreProps.length) {
                    this.opened = false;
                    this.dataFetched = [];
                    this.selectedObjectIds = [];
                    this.layerChildTables = [];
                    this.hideCustomSort = true;

                    this.lassoTool.destroy();
                }
            }
        } else if (this._mapcoreProps.length > 1) {
            if (this._mapcoreProps.length === 2) {
                this.enableMultiTabs = false;
                this.multiTabsArr = [];
                this.curMultiTab = '';
            }
            this.closeTab.emit(this.mapcoreProps.esriLayerId);
            this._mapcoreProps.splice(this.mapcorePropsIndex, 1);
            this.mapcorePropsIndex = 0;
        } else {
            this.closeTab.emit(this.mapcoreProps.esriLayerId);
            this.mapcoreProps.dataTableArr = [];
            this._mapcoreProps = [];

            this.opened = false;
            this.dataFetched = [];
            this.selectedObjectIds = [];
            this.layerChildTables = [];
            this.hideCustomSort = true;

            this.lassoTool.destroy();
        }
        this.showCloseOptions = false;
    }

    @Input()
    closeAll = () => {
        if (this._mapcoreProps) {
            while (this._mapcoreProps.length > 0) {
                this.close();
            }
        }
    }

    private setColumnCheckboxes() {
        this.allVisible = this.columnSettings.every(col => col.visible);
        this.allSum = this.columnSettings.filter(col => col.isNumeric && col.calculateSum !== null).every(col => col.calculateSum);
        this.allAverage = this.columnSettings.filter(col => col.isNumeric && col.calculateAverage !== null).every(col => col.calculateAverage);
    }

    async columnSettingChange() {
        this.toggleLoading(true);
        this.setColumnCheckboxes();
        if (this.columnSettingsTimer) {
            window.clearTimeout(this.columnSettingsTimer);
        }
        this.columnSettingsTimer = window.setTimeout(async () => {
            this.columnSettingsTimer = null;
            await this.populateGridColumnsFromColumnSettings();
            await this.fetchDataIfNeeded();
            this.loadDataIntoGrid();
            this.populateAggregates();
            this.toggleLoading(false);
        }, this.delayForFetchingNewColumnData);
    }

    async download() {
        const rootQuery: IQuery = this.layerService.modifyQueryForExport(this.mapcoreProps.parentQuery, this.mapcoreProps.query);

        if (this.mapcoreProps.state.filters && this.mapcoreProps.state.filters.length > 0) {
            const filters = [];

            this.mapcoreProps.state.filters.forEach(filter => {
                const filterCopy = { ...filter };

                if (filter.operator === 'like') {
                    filterCopy.operator = 'LIKE';
                }

                if (this.columnSettings.filter(column => column.name.toLowerCase().trim() === filter.field.toLowerCase().trim())[0].isNumeric) {
                    filterCopy.field = `to_char(${filter.field})`;
                }

                filters.push(filterCopy);
            });

            rootQuery.subqueries.push(...filters);
        }

        const stringifiedSort = this.mapcoreProps.state.sort && this.mapcoreProps.state.sort.length > 0
            ? JSON.stringify(this.mapcoreProps.state.sort)
            : '';

        const exportParams: IExportParameters = {
            layerId: this.systemLayerId,
            query: rootQuery,
            title: this.mapcoreProps.name,
            sort: stringifiedSort
        };

        if (this.selectedObjectIds.length > 0) {
            const query: IQuery = {
                field: this.objectIdColumnName,
                operator: 'IN',
                value: this.selectedObjectIds.join(',')
            };
            rootQuery.subqueries.push(query);
        }

        const fieldNames = this.tableColumnSettings.filter((tableColumSetting) => tableColumSetting.isVisible).map((tableColumSetting) => tableColumSetting.propertyName);

        if (this.layerColumns) {
            // if layerColumns are present, pass those as as parameters, but perserve the order of the columns in fieldNames
            exportParams.layerColumns = [];

            for (const fieldName of fieldNames) {
                const layerColumnsFound = this.layerColumns.filter(x => x.name.toLowerCase().trim() === fieldName.toLowerCase().trim());
                if (layerColumnsFound.length) {
                    exportParams.layerColumns.push(layerColumnsFound[0]);
                }
            }
        } else {
            exportParams.fieldNames = fieldNames;
        }

        if (this.mapcoreProps.isExternal) {
            exportParams.data = [];

            this.filteredData.forEach(row => {
                const dataRow = {};

                for (const column in row) {
                    if (exportParams.layerColumns.find(layerColumn => layerColumn.name.toLowerCase().trim() === column.toLowerCase().trim())) {
                        dataRow[column] = row[column];
                    }
                }

                exportParams.data.push(dataRow);
            });

            if (exportParams.data.length === 0) {
                exportParams.data.push({});
            } else if (exportParams.data.some(data => data.hasOwnProperty('hasRowDetails'))) {
                exportParams.data.forEach(data => {
                    if (data.hasOwnProperty('hasRowDetails')) {
                        delete data.hasRowDetails;
                    }
                });
            }
        }

        try {
            await this.exportService.exportToExcelAndOpenUrl(exportParams);
        } catch (error) {
            this.LoggingService.Log('Error exporting data table to excel.', error, LogLevel.Error);
        }
    }

    private readonly columnSortByDBOrder = (a: ILayerColumn, b: ILayerColumn): number => {
        if (a.databaseColumnOrder > b.databaseColumnOrder) {
            return 1;
        } else if (a.databaseColumnOrder > b.databaseColumnOrder) {
            return -1;
        }
        return 0;
    }

    async populateLayerInformation() {
        if (this.systemLayerId) {
            const layers = await this.layerApi.getLayerInfo(this.systemLayerId);
            if (layers.length > 0) {
                if (this.mapcoreProps === undefined) {
                    return [];
                }
                this.layerRef = layers[0];
                this.layerColumns.length = 0;
                if (!this.mapcoreProps.columns) {
                    this.layerColumns.push(...layers[0].columns);
                } else {
                    //Use this.mapcoreProps.columns with layers[0]'s databaseDataType mapped to each respective column
                    for (const col of this.mapcoreProps.columns) {
                        //find the related column in layers[0]
                        const layersCol = layers[0].columns.find(x => x.databaseColumnOrder === col.databaseColumnOrder);
                        if (!layersCol) {
                            continue;
                        }
                        //if found, append its listed dataType to mapcoreprops columns list
                        if (layersCol.databaseDataType) {
                            col.databaseDataType = layersCol.databaseDataType;
                        }
                    }
                    this.layerColumns.push(...this.mapcoreProps.columns);
                }
                this.layerColumns.sort(this.columnSortByDBOrder);
                this.objectIdColumnName = this.layerService.getObjectIdColumnName(
                    this.layerRef
                );
                for (const layerColumn of this.layerColumns) {
                    layerColumn.name = layerColumn.name.toLowerCase();
                    if (
                        layerColumn.alias &&
                        layerColumn.alias.toUpperCase() ===
                        layerColumn.name.toUpperCase()
                    ) {
                        layerColumn.alias = layerColumn.name.toUpperCase();
                    }
                }
                return this.layerColumns;
            }
        } else if (this.mapcoreProps.isExternal) {
            this.layerColumns = [...this.mapcoreProps.columns];
            this.layerColumns.sort(this.columnSortByDBOrder);
            const objectIdColumn = this.layerColumns.find(col => col.name.toLowerCase().replace('_', '').trim() === this.idPropertyName);
            this.objectIdColumnName = objectIdColumn ? objectIdColumn.name : null;
            return this.layerColumns;
        }
        return [];
    }

    async fetchDataForAllVisibleColumns() {
        this.dataFetched = [];
        this.selectedObjectIds = [];
        if (!this.tableColumnSettings || !this.tableColumnSettings.length) {
            await this.populateGridColumnsFromColumnSettings();
        }
        let fieldNames = this.tableColumnSettings.map((x) => x.propertyName.toLowerCase().trim());
        if (fieldNames.indexOf(this.objectIdColumnName.toLowerCase().trim()) < 0) {
            fieldNames.push(this.objectIdColumnName.toLowerCase().trim());
        }
        if (this.childTableChildParentKeyColumnNames !== null) {
            fieldNames = [...new Set(fieldNames.concat(this.childTableChildParentKeyColumnNames))];
        }
        if (!this.mapcoreProps.isExternal) {
            await this.fetchDataForSpecifiedColumns(fieldNames);
        } else {
            await this.fetchExternalDataForSpecifiedColumns(fieldNames);
        }
    }

    async fetchExternalDataForSpecifiedColumns(fieldNames: string[]) {
        const result = await this.fetchExternalData(fieldNames);
        this.dataFetched = result.map(row => {
            const item: { [key: string]: DataItem } = {};
            Object.entries(row).forEach(([col, value]) => {
                let colName = col;
                let builtValue: DataItem = value;
                const matchingColumn = this.layerColumns.find(c => c.name.toLowerCase().trim() === col.toLowerCase().trim());
                if (matchingColumn && matchingColumn.domainValues) {
                    colName = matchingColumn.name;
                    let foundDomainValue;
                    if (typeof value === 'number') {
                        foundDomainValue = matchingColumn.domainValues.find(dv => parseFloat(dv.codeValue.toString()) === value);
                    } else {
                        foundDomainValue = matchingColumn.domainValues.find(dv => dv.codeValue === value);
                    }
                    if (foundDomainValue) {
                        builtValue = { value, domainValue: `${foundDomainValue.codeValue} - ${foundDomainValue.shortDescription}` };
                    }
                }
                item[colName] = builtValue;
            });
            return item;
        });
    }

    async fetchDataForSpecifiedColumns(fieldNames: string[]) {
        const rootQuery: IQuery = { operator: 'AND', subqueries: [] };
        if (this.mapcoreProps.parentQuery) {
            rootQuery.subqueries.push(this.mapcoreProps.parentQuery);
        }
        if (this.mapcoreProps.query) {
            rootQuery.subqueries.push(this.mapcoreProps.query);
        }
        this.defaultRecordsFetched = this.maxRecordsFetched;
        if (fieldNames.length > 2) {
            const minRecords = 100000;
            const multFactor = 5;
            const adjustment = 4;
            const roundFactor = 1000;
            const calculatedAmount = Math.round((this.defaultRecordsFetched / ((multFactor * Math.sqrt(fieldNames.length)) - adjustment)) / roundFactor) * roundFactor;
            this.defaultRecordsFetched = Math.max(calculatedAmount, minRecords);
        }

        const result: (string | number)[][] = await this.layerApi.getData(
            this.systemLayerId,
            fieldNames,
            rootQuery,
            this.defaultRecordsFetched
        );

        if (this.mapcoreProps && this.mapcoreProps.columns && !this.mapcoreProps.columns.some(c => !!c.domainValues) && this.mapcoreProps.metaTableId) {
            const dictionary = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.mapcoreProps.metaTableId);
            if (dictionary) {
                for (const column of dictionary) {
                    const matchingColumn = this.mapcoreProps.columns.find(c => c.name.toLowerCase().trim() === column.columnName.toLowerCase().trim());
                    if (matchingColumn) {
                        matchingColumn.domainValues = column.domainValues;
                    }
                }
            }
        }

        let columns: ILayerColumn[] = [];

        if (this.mapcoreProps && this.mapcoreProps.columns) {
            columns = fieldNames.map(field => this.mapcoreProps.columns.find(c => c.name.toLowerCase().trim() === field.toLowerCase().trim()) || ({ name: field.toLowerCase().trim() } as ILayerColumn));
        } else {
            columns = fieldNames.map(field => ({ name: field.toLowerCase().trim() } as ILayerColumn));
        }

        this.dataFetched = result.map(row => {
            const item: { [key: string]: DataItem } = {};
            row.forEach((value, i) => {
                let builtValue: DataItem = value;
                const col = columns[i];
                if (col.domainValues) {
                    let foundDomainValue;
                    if (typeof value === 'number') {
                        foundDomainValue = col.domainValues.find(dv => parseFloat(dv.codeValue.toString()) === value);
                    } else {
                        foundDomainValue = col.domainValues.find(dv => dv.codeValue === value);
                    }
                    if (foundDomainValue) {
                        builtValue = { value, domainValue: `${foundDomainValue.codeValue} - ${foundDomainValue.shortDescription}` };
                    }
                }
                item[col.name] = builtValue;
            });
            return item;
        });
    }

    async fetchDataIfNeeded() {
        if (this.dataFetched.length > 0) {
            const fieldNames = this.tableColumnSettings.map((x) => x.propertyName.toLowerCase().trim());
            const dataKeys = Object.keys(this.dataFetched[0]).map(x => x.toLowerCase().trim());
            if (fieldNames.some(fieldName => !dataKeys.includes(fieldName))) {
                await this.fetchDataForAllVisibleColumns();
            }
        }
    }

    async fetchExternalData(outFields?: string[]) {
        const formatResponse = (response: __esri.FeatureSet) => {
            return response.features.map(feature => {
                return Object.keys(feature.attributes).reduce<{ [key: string]: any }>((acc, key) => {
                    acc[key.toLowerCase()] = feature.attributes[key];
                    return acc;
                }, {});
            });
        };

        const featureLayer = this.layerService.mapLayers.feature.find(element => element.mapcoreProps.guid === this.mapcoreProps.guid);
        const results: { [key: string]: any }[] = [];
        if (featureLayer) {
            const query = featureLayer.createQuery();
            query.outFields = outFields;
            query.num = this.maxRecordsFetched;
            let keepRequesting = true;
            while (keepRequesting) {
                query.start = results.length;
                const response = await featureLayer.queryFeatures(query);
                if (!results.length) {
                    query.num = response.features.length;
                }
                results.push(...formatResponse(response));
                keepRequesting = response.exceededTransferLimit;
            }
        }
        return results;
    }

    async fetchLayerChildTables() {
        this.layerChildTables = await this.layerService.getLayerChildTables(this.systemLayerId);
        const filteredChildTables = this.layerChildTables.filter(childTable => childTable.tableId !== this.geoSnapTableId); // Fix for geosnap having invalid childParentKeyColumnName
        this.childTableChildParentKeyColumnNames = [...new Set(filteredChildTables.map(table => table.childParentKeyColumnName.toLowerCase().trim()))];
        this.childTableChildParentKeyColumnNames.push(this.idPropertyName); // add this in for 161 geosnap
    }

    async updateLayerInformationIfNeeded() {
        if (this.mapcoreProps.isExternal) {
            await this.setHashedUrl();
        }

        this.updateStorage = false;
        this.columnSettings = (await this.populateLayerInformation()).map(lc => this.getRefreshedColumnSetting(lc));
        this.updateStorage = true;
        this.refreshColumnSettings();
        await this.createOrUpdateFromLocalStorage();
        await this.populateGridColumnsFromColumnSettings();
        this.sortColumns(this.tableColumnSettings);

        if (this.systemLayerId && this.enableAdditionalInformation) {
            await this.fetchLayerChildTables();
        }
    }

    cdkDrop(event: CdkDragDrop<null>) {
        this.moveColumn(event.previousIndex, event.currentIndex);
    }

    onFilterEvent(event) {
        this.mapcoreProps.state.filters = this.filterSettings = event.filterSettings;
        this.filteredData = event.filteredData;
        this.rowCount = this.addCommas(this.filteredData.length);
        this.populateAggregateValues();
        this.changeDetectorRef.detectChanges();

        (async () => {
            await this.accessingLocalStorage;
            await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'filterSettings', event.filterSettings, 'data-table');
        })();
    }

    onSortEvent(event) {
        this.mapcoreProps.state.sort = [];
        this.sortSettings = event.sortSettings;

        event.sortSettings.forEach((setting: ISortSetting) => {
            this.mapcoreProps.state.sort.push({
                dir: setting.descending ? 'desc' : 'asc',
                field: setting.field
            });
        });

        (async () => {
            await this.accessingLocalStorage;
            await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'sortSettings', event.sortSettings, 'data-table');
        })();
    }

    onColumnResize(event) {
        const column = this.columnSettings.find(col => col.name.toLowerCase() === event.propertyName.toLowerCase());

        if (column) {
            column.width = event.width;

            (async () => {
                await this.accessingLocalStorage;
                await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'columnSettings', this.mapColumnsForSave(this.columnSettings), 'data-table');
            })();
        }
    }

    moveItemToTop(currentIndex) {
        this.moveColumn(currentIndex, 0);
    }

    private moveColumn(previousIndex, newIndex) {
        if (this.columnSortOrder !== 'custom') {
            this.sortColumns(this.columnSettings);
            this.columnSortOrder = 'custom';
        }

        this.hideCustomSort = false;
        this.columnSettings.splice(newIndex, 0, ...this.columnSettings.splice(previousIndex, 1));

        (async () => {
            await this.accessingLocalStorage;
            await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'enableCustom', !this.hideCustomSort, 'data-table');
            await this.columnSettingChange();
            this.setColumnSettingsFilteredForDialog();
        })();
    }

    getRefreshedColumnSetting(
        layerColumn: ILayerColumn,
        columnSetting: IColumnSetting = null
    ) {
        if (!columnSetting) {
            columnSetting = { ...layerColumn };
        }
        columnSetting.visible =
            columnSetting.visible || columnSetting.visible === undefined;
        columnSetting.isNumeric =
            ['FLOAT', 'NUMBER'].indexOf(layerColumn.databaseDataType) >= 0;
        columnSetting.displayName = layerColumn.name.toUpperCase();
        if (layerColumn.alias) {
            columnSetting.name = columnSetting.name.toUpperCase();
            columnSetting.displayName = layerColumn.alias;
        }
        if (!columnSetting.width) {
            columnSetting.width = this.calculateWidth(columnSetting);
        }
        if (layerColumn.databaseColumnOrder) {
            columnSetting.databaseColumnOrder = layerColumn.databaseColumnOrder;
        }
        return columnSetting;
    }

    loadDataIntoGrid() {
        if (this.mapcoreProps) {
            let dataForGrid = this.dataFetched;

            if (this.showSelected) {
                dataForGrid = dataForGrid.filter(x => this.selectedObjectIds.indexOf(x[this.objectIdColumnName] as number) >= 0);
            }

            this.detectUrls(dataForGrid);

            const hasRowDetails = this.hasChildTables;

            dataForGrid.forEach(row => {
                for (const field of Object.keys(row)) {
                    if (row[field] != null && (row[field] as any).domainValue) {
                        row[field] = (row[field] as any).domainValue;
                    }

                    if (field === 'has_geometry' && row[field] === 'N') {
                        row.rowClass = RowClass.HighlightRed;
                    }
                }

                (row as any).hasRowDetails = hasRowDetails;
            });

            this.fullData = this.filteredData = dataForGrid;

            this.gridDataResult = [...dataForGrid];
            this.rowCount = this.addCommas(dataForGrid.length);
        }
    }

    expandDetailsBy = (dataItem: any): any => {
        return dataItem.objectid;
    }

    /**
     * Modifies the data passed in to add isUrl boolean if a url is detected for a column
     * @param dataForGrid Array of data rows
     */
    detectUrls(dataForGrid: { [key: string]: any }[]): void {
        if (dataForGrid && dataForGrid.length) {
            // Find the url column first
            const urlColumns: string[] = [];
            const nonurlColumns: string[] = [];
            const columnLength = Object.keys(dataForGrid[0]).length;
            for (const row of dataForGrid) {
                // Iterate over every row of data
                for (const col in row) {
                    // Iterate over every column in the row
                    if (
                        !nonurlColumns.includes(col) &&
                        !urlColumns.includes(col)
                    ) {
                        // Only check columns that we don't know about yet
                        const value = row[col];
                        if (value !== null) {
                            // Only check if not null, as the url column could be nullable
                            const columnSetting = this.columnSettings.find(
                                (cs) => cs.name === col
                            );
                            if (columnSetting && (columnSetting.isUrl || this.isUrl(value))) {
                                urlColumns.push(col);
                            } else {
                                nonurlColumns.push(col);
                            }
                        }
                    }
                }
                if (columnLength === urlColumns.length + nonurlColumns.length) {
                    break;
                } // If all columns have been checked, stop checking completly
            }
            if (urlColumns.length) {
                for (const columnSetting of this.columnSettings) {
                    if (urlColumns.includes(columnSetting.name)) {
                        columnSetting.isUrl = true;
                    } else {
                        columnSetting.isUrl = false;
                    }
                }
            }
        }
    }

    isUrl(value) {
        return (
            value &&
            typeof value === 'string' &&
            /^http[s]?:\/\/(?:[a-zA-Z]|[0-9]|[$-_@.&+]|[!*(), ]|(?:%[0-9a-fA-F][0-9a-fA-F]))+$/.test(
                value.trim()
            )
        );
    }

    mapSelectedFeatures() {
        if (this.selectedObjectIds.length > 0) {
            const query: IQuery = {
                field: this.objectIdColumnName,
                operator: 'IN',
                value: this.selectedObjectIds.join(',')
            };
            if (!this.mapcoreProps.isExternal) {
                const layerSettings = {
                    id: this.systemLayerId,
                    query,
                    zoomToLayer: true
                };
                this.layerService.addMapcoreLayersToMap([layerSettings]);
            } else {
                const featureLayer = this.layerService.mapLayers.feature.find(element => element.mapcoreProps.guid === this.mapcoreProps.guid);
                this.layerService.addExternalLayersToMap({
                    url: this.mapcoreProps.url,
                    name: featureLayer.title,
                    visible: true,
                    zoomToLayer: true
                } as ILayerSettings,
                    { ...this.mapcoreProps, query } as IMapcoreProps);
            }
            if (this.lassoTool) {
                this.lassoTool.clearFeatureResults();
            }
        }
    }

    async populateGridColumnsFromColumnSettings() {
        //update local storage columnsettings if needed
        if (this.mapcoreProps !== undefined) {
            await this.accessingLocalStorage;
            const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
            if (this.updateStorage) {
                if (!settingsStorage || settingsStorage.columnSettings.length === 0) {
                    //first time
                    this.hideCustomSort = true;
                    await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'columnSettings', this.mapColumnsForSave(this.columnSettings), 'data-table');
                } else if (JSON.stringify(settingsStorage.columnSettings) !== JSON.stringify(this._columnSettings)) {
                    //consecutive times
                    if (settingsStorage.sortBy) {
                        this.columnSortOrder = settingsStorage.sortBy;
                        if (settingsStorage.sortBy === 'custom') {
                            this.hideCustomSort = false;
                        }
                    }
                    await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'columnSettings', this.mapColumnsForSave(this.columnSettings), 'data-table');
                } else if (settingsStorage.sortBy) {
                    this.columnSortOrder = settingsStorage.sortBy;
                }
            }
        }

        this.tableColumnSettings = [];

        const visibleColumnSettings = this.columnSettings.filter(x => x.visible);

        if (visibleColumnSettings.length === 0) {
            const idColumn = { ...this.columnSettings.find(c => c.name.toLowerCase().trim() === this.idPropertyName) };
            idColumn.name = idColumn.name.toLowerCase();
            idColumn.visible = true;
            this.addColumnSettingToTable(idColumn);
        } else {
            for (const columnSetting of this.columnSettings) {
                const gridColumn: IColumnSetting = { ...columnSetting };
                gridColumn.name = gridColumn.name.toLowerCase().trim();
                this.addColumnSettingToTable(gridColumn);
            }

            const geometryColumn = this.columnSettings.find(c => c.name.toLowerCase().trim() === 'has_geometry');

            if (geometryColumn && !this.tableColumnSettings.find(c => c.propertyName.toLowerCase().trim() === geometryColumn.name.toLowerCase().trim())) {
                const geometryColumnSetting = { ...geometryColumn };
                geometryColumnSetting.visible = false;
                this.addColumnSettingToTable(geometryColumnSetting);
            }

            this.sortColumns(this.tableColumnSettings);
        }
    }

    addColumnSettingToTable(columnSetting: IColumnSetting) {
        let columnDataType: TableCellDataType = 'string';

        if (columnSetting.databaseDataType === 'DATE') {
            columnDataType = 'date';
        } else if (columnSetting.isUrl) {
            columnDataType = 'url';
        }

        const tableColumnSetting: ITableColumnSettings = {
            canFilter: true,
            canSort: true,
            columnOrder: columnSetting.databaseColumnOrder,
            dataFilter: columnSetting.databaseDataType === 'DATE' ? 'short' : '',
            dataType: columnDataType,
            footerText: '',
            isVisible: columnSetting.visible,
            propertyName: columnSetting.name,
            title: columnSetting.displayName,
            width: columnSetting.width ? `${columnSetting.width}px` : ''
        };

        const filter = this.filterSettings.find(filterSetting => filterSetting.field === columnSetting.name);

        if (filter) {
            tableColumnSetting.filter = filter.value;
        }

        this.tableColumnSettings.push(tableColumnSetting);
    }

    populateValidColumnSettings() {
        for (let i = 0; i < this.columnSettings.length; i++) {
            let columnSetting = this.columnSettings[i];
            const foundLayerColumns = this.layerColumns.filter(
                (x) => x.name.toLowerCase() === columnSetting.name.toLowerCase()
            );
            if (foundLayerColumns.length > 0) {
                const layerColumn = foundLayerColumns[0];
                columnSetting = this.getRefreshedColumnSetting(
                    layerColumn,
                    columnSetting
                );
            } else {
                this.columnSettings.splice(i, 1);
                i--;
            }
        }
    }

    populateColumnSettingsToMax() {
        let visibleColumnSettings = this.columnSettings.filter((x) => x.visible)
            .length;
        for (const layerColumn of this.layerColumns) {
            if (
                this.columnSettings.filter(
                    (x) =>
                        x.name.toLowerCase() === layerColumn.name.toLowerCase()
                ).length === 0
            ) {
                const columnSetting = this.getRefreshedColumnSetting(
                    layerColumn
                );
                if (visibleColumnSettings <= this.maxDefaultColumns) {
                    visibleColumnSettings++;
                } else {
                    columnSetting.visible = false;
                }
                this.columnSettings.push(columnSetting);
            }
        }
    }

    populateAggregates() {
        this.setAggregateArray();
        this.populateAggregateValues();
    }

    populateAggregateValues() {
        const fractionDigits = 4;

        if (this.aggregates && this.aggregates.length) {
            this.aggregates = this.getAggregateData(this.filteredData, this.aggregates);

            this.aggregates.forEach((aggregateField) => {
                const tableColumn = this.tableColumnSettings.find(c => c.propertyName.toLowerCase().trim() === aggregateField.field.toLowerCase().trim());

                if (tableColumn) {
                    const footerText: string[] = [];
                    tableColumn.footerText = '';

                    aggregateField.aggregates.forEach((aggregate) => {
                        if (aggregate.type === 'average') {
                            const averageText = aggregate.value.toFixed(fractionDigits);
                            tableColumn.footerText += 'Avg: ' + this.addCommas(averageText);
                            footerText.push(`Avg: ${this.addCommas(averageText)}`);
                        }

                        if (aggregate.type === 'sum') {
                            if (Math.floor(aggregate.value) === aggregate.value) {
                                footerText.push(`Sum: ${this.addCommas(aggregate.value)}`);
                            } else {
                                footerText.push(`Sum: ${this.addCommas(aggregate.value.toFixed(fractionDigits))}`);
                            }
                        }
                    });

                    tableColumn.footerText = footerText.join(' ');
                }
            });

            this.changeDetectorRef.detectChanges();
        }
    }

    getAggregateData(data: any[], aggregates: IAggregateField[]) {
        aggregates.forEach((aggregateField) => {
            const aggregateFieldData = data.map((row) => row[aggregateField.field]);
            const sum = aggregateFieldData.reduce((a, b) => Number(a) + Number(b), 0);

            aggregateField.aggregates.forEach((aggregate) => {
                if (aggregate.type === 'average') {
                    aggregate.value = aggregateFieldData.length === 0 ? 0 : sum / aggregateFieldData.length;
                }

                if (aggregate.type === 'sum') {
                    aggregate.value = sum;
                }
            });
        });

        return aggregates;
    }

    refreshColumnSettings() {
        this.populateValidColumnSettings();
        this.populateColumnSettingsToMax();
    }

    setAggregateArray() {
        this.aggregates = [];

        for (const columnSetting of this.columnSettings) {
            if (columnSetting.calculateSum || columnSetting.calculateAverage) {
                const aggregateField: IAggregateField = {
                    aggregates: [],
                    field: columnSetting.name.toLowerCase()
                };

                if (columnSetting.calculateSum) {
                    aggregateField.aggregates.push({
                        type: 'sum',
                        value: 0
                    });
                }

                if (columnSetting.calculateAverage) {
                    aggregateField.aggregates.push({
                        type: 'average',
                        value: 0
                    });
                }

                this.aggregates.push(aggregateField);
            }
        }

        this.showFooter = this.aggregates.length > 0;
    }

    sortColumns = (columns: any[]) => {
        if (columns && columns.length > 0) {
            const columnDatabaseOrder = 'databaseColumnOrder' in columns[0] ? 'databaseColumnOrder' : 'columnOrder';
            const columnDisplayName = 'displayName' in columns[0] ? 'displayName' : 'title';
            const columnPropertyName = 'name' in columns[0] ? 'name' : 'propertyName';

            const alphaSort = (a: any, b: any) => {
                const aComp = a[columnDisplayName].toUpperCase();
                const bComp = b[columnDisplayName].toUpperCase();

                if (aComp.localeCompare(bComp) > 1) {
                    return 1;
                } else if (aComp.localeCompare(bComp) < 1) {
                    return -1;
                }

                return 0;
            };

            const dbSort = (a: any, b: any) => {
                if (a[columnDatabaseOrder] > b[columnDatabaseOrder]) {
                    return 1;
                } else if (a[columnDatabaseOrder] < b[columnDatabaseOrder]) {
                    return -1;
                }

                return 0;
            };

            const findColumnIndex = (cs: any) => {
                return this.columnSettings.findIndex((c: IColumnSetting) => c.name.toUpperCase() === cs[columnPropertyName].toUpperCase());
            };

            const customSort = (a: any, b: any) => {
                const aIndex = findColumnIndex(a);
                const bIndex = findColumnIndex(b);

                if (aIndex > bIndex) {
                    return 1;
                } else if (aIndex < bIndex) {
                    return -1;
                }

                return 0;
            };

            switch (this.columnSortOrder) {
                case 'alphabetical':
                    columns.sort(alphaSort);
                    break;
                case 'custom':
                    columns.sort(customSort);
                    break;
                case 'database':
                    columns.sort(dbSort);
                    break;
                default:
                    break;
            }
        }

        return columns;
    }

    changeCurMultiTab() {
        //find the the mapcorePropsIndex that has the selected layerId
        const i = this._mapcoreProps.findIndex(x => this.curMultiTab === x.esriLayerId);
        if (i > -1) {
            this.mapcoreProps = this._mapcoreProps[i];
        }
        if (this.lassoTool) {
            this.lassoTool.deactivate();
            this.lassoTool.clearFeatureResults();
        }
    }

    closeColumnSettings(event) {
        if (!event.detail) {
            this.toggleColumnSettings();
        }
    }

    toggleColumnSettings() {
        this.columnSettingsDialogOpened = !this.columnSettingsDialogOpened;
        this.changeDetectorRef.detectChanges();
    }

    toggleColumnVisibility(columnSetting: IColumnSetting) {
        columnSetting.visible = !columnSetting.visible;
        this.columnSettingChange();
    }

    toggleShowSelected() {
        this.showSelected = !this.showSelected;
        this.loadDataIntoGrid();
        this.populateAggregates();
    }

    toggleSortColumnSettings(sortField: SettingSortType) {
        if (this.columnSettingsSortField === sortField) {
            this.columnSettingsSortDescending = !this.columnSettingsSortDescending;
        } else {
            this.columnSettingsSortDescending = false;
        }
        this.columnSettingsSortField = sortField;
    }

    async createOrUpdateFromLocalStorage(skip: 'columnSettings' | 'order' = null) {
        this.accessingLocalStorage = (async () => {
            //See if this layer already has settings stored in localStorageService
            const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);

            if (hasKey) {
                const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);

                if (skip === 'columnSettings') {
                    this._columnSettings = this.mapColumnsFromSaved(this.columnSettings, settingsStorage && settingsStorage.columnSettings ? settingsStorage.columnSettings : []);
                } else {
                    this.columnSettings = this.mapColumnsFromSaved(this.columnSettings, settingsStorage && settingsStorage.columnSettings ? settingsStorage.columnSettings : []);
                }

                if (skip === 'order') {
                    this._columnSortOrder = settingsStorage && settingsStorage.sortBy ? settingsStorage.sortBy : 'database';
                } else {
                    this.columnSortOrder = settingsStorage && settingsStorage.sortBy ? settingsStorage.sortBy : 'database';
                }

                if (settingsStorage) {
                    if (settingsStorage.enableCustom) {
                        this.hideCustomSort = false;
                    }

                    if (settingsStorage.filterSettings) {
                        this.filterSettings = settingsStorage.filterSettings;
                    }

                    if (settingsStorage.sortSettings) {
                        this.sortSettings = settingsStorage.sortSettings;
                    }
                }

                this.columnSettings.forEach((columnSetting) => {
                    const tableColumnSetting = this.tableColumnSettings.find(tableColumn => tableColumn.propertyName.toLowerCase() === columnSetting.name.toLowerCase());

                    if (tableColumnSetting) {
                        if (columnSetting.width) {
                            tableColumnSetting.width = `${columnSetting.width}px`;
                        }

                        const tableFilterSetting = this.filterSettings.find(filterSetting => filterSetting.field.toLowerCase() === columnSetting.name.toLowerCase());

                        if (tableFilterSetting) {
                            tableColumnSetting.filter = tableFilterSetting.value;
                        }
                    }
                });
            } else {
                //If local storage does not exist, create it
                //and add the current visibility settings to the column visibility object in local storage for use in the Identify window
                const columnStorageSettings: IColumnSettingsStorage = {
                    columnSettings: this.mapColumnsForSave(this.columnSettings),
                    enableCustom: !this.hideCustomSort,
                    filterSettings: this.filterSettings,
                    showAll: false,
                    sortBy: this.columnSortOrder,
                    sortSettings: this.sortSettings
                };

                if (this.systemLayerId > 0) {
                    await this.localStorageService.set(this.columnSettingsStorageKey, columnStorageSettings, 'data-table');
                }
            }
        })();

        return await this.accessingLocalStorage;
    }

    toggleCloseOptions() {
        this.showCloseOptions = !this.showCloseOptions;
    }

    onRowSelection(event) {
        if (this.lassoTool) {
            this.lassoTool.clearFeatureResults();
        }

        this.selectedObjectIds = event.selectedRowIds;
    }

    selectFromMap() {
        this.lassoTool.deactivate();
        this.lassoTool.containerElement = this.hostElement;
        const subs: Subscription[] = [];
        subs.push(this.lassoTool.oncomplete.subscribe(() => {
            const featureresults: { [key: string]: any; attributes: { [key: string]: any } }[] = (<any[]>this.lassoTool.layerFeatureResults.map(r => r.featureResults)).flat();
            if (featureresults.length) {
                const frObjectIdKey = Object.keys(featureresults[0].attributes).find(key => key.toLowerCase() === this.idPropertyName);
                if (frObjectIdKey) {
                    const selectedIds = featureresults.map(result => result.attributes[frObjectIdKey]);
                    this.selectedObjectIds = [...new Set(selectedIds)];
                }
            }
            if (this.dataFetched.length) {
                const objectIdKey = Object.keys(this.dataFetched[0]).find(k => k.toLowerCase() === this.idPropertyName);
                if (objectIdKey) {
                    this.selectedObjectIds = this.selectedObjectIds.filter(id => !!this.dataFetched.find(d => d[objectIdKey] === id));
                }
            }
            this.lassoTool.deactivate();
            subs.forEach(sub => sub.unsubscribe());
            this.toggleLoading(false);
        }));
        subs.push(this.lassoTool.drawEnd.subscribe(() => {
            this.selectedObjectIds = [];
            this.ondrawcomplete.emit();
            this.toggleLoading(true);
        }));
        this.lassoTool.spatialQueryType = 'feature';
        this.lassoTool.runSpatialQueryOnSketchComplete = true;
        this.lassoTool.reactivateSketchOnSketchComplete = false;
        this.lassoTool.displayFeatureGraphicsAfterSpatialQuery = true;
        this.lassoTool.sketchDefaultCreateOptions = { mode: 'freehand' };
        this.lassoTool.mapView = this.mapView;
        this.lassoTool.esriLayers = [this.layerService.getExistingEsriLayerById(this.mapcoreProps.esriLayerId)];
        this.lassoTool.clearFeatureResults();
        this.lassoTool.activate();
        this.ondraw.emit();
    }

    triggerOpenEditor(asEdit = false) {
        const objectId = asEdit ? this.selectedObjectIds[0] : null;
        this.openEditor.emit({ layerId: this.mapcoreProps.esriLayerId, objectId });
    }

    private mapColumnsForSave(columnSettings: IColumnSetting[]) {
        return columnSettings.map(column => ({
            name: column.name,
            visible: column.visible,
            width: column.width,
            displayName: column.displayName,
            databaseDataType: column.databaseDataType,
            databaseColumnOrder: column.databaseColumnOrder,
            columnIdentifierId: column.columnIdentifierId,
            isNumeric: column.isNumeric,
            calculateAverage: column.calculateAverage,
            calculateSum: column.calculateSum
        } as ISmallColumnSetting));
    }

    private mapColumnsFromSaved(columnSettings: IColumnSetting[], fromSaved: ISmallColumnSetting[]) {
        columnSettings.forEach(column => {
            const match = fromSaved.find(x => x.name === column.name);
            if (match) {
                column.visible = match.visible;
                column.width = match.width;
                column.calculateAverage = match.calculateAverage;
                column.calculateSum = match.calculateSum;
            }
        });
        return columnSettings.sort((a, b) => {
            const aIndex = fromSaved.findIndex(x => x.name === a.name);
            const bIndex = fromSaved.findIndex(x => x.name === b.name);
            return aIndex - bIndex;
        });
    }

    isDataItem(value: DataItem): value is IDataItem {
        return value !== null && typeof value === 'object';
    }

    toggleLoading(isLoading: boolean) {
        this.loading = isLoading;
        this.changeDetectorRef.detectChanges();
    }

}

type AggregateType = 'average' | 'sum';

export interface IAggregate {
    type: AggregateType;
    value: number;
}

export interface IAggregateField {
    aggregates: IAggregate[];
    field: string;
}

type DataItem = IDataItem | string | number;
interface IDataItem { value: string | number; domainValue: string; }

type SettingSortType = 'displayName' | 'order' | 'visible' | 'calculateSum' | 'calculateAverage';
